import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Avatar, Button, EmojiPicker } from '@/components/ui';
import { SubPageHeader } from '@/components/ui/SubPageHeader';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface SelectEmojiProps {
  currentEmoji: string;
  currentColor: string;
  onSave: (emoji: string, color: string) => void;
  onBack: () => void;
  /** Same switch as `ChangeProfileImage`'s — see there. */
  headerVariant?: 'headline' | 'subpage';
}

export function SelectEmoji({
  currentEmoji,
  currentColor,
  onSave,
  onBack,
  headerVariant = 'headline',
}: SelectEmojiProps) {
  const [selectedEmoji, setSelectedEmoji] = useState(currentEmoji);
  const [selectedColor, setSelectedColor] = useState(currentColor);
  // Mounted only while shown, and it covers the whole screen: hide and
  // pause the bottom toast slot underneath it (YAR-476).
  useRegisterOverlay(true);

  return (
    <div
      className="fixed inset-0 z-[200] bg-background overflow-y-auto flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby={
        headerVariant === 'headline' ? 'select-emoji-heading' : undefined
      }
      aria-label={headerVariant === 'subpage' ? 'Select emoji' : undefined}
    >
      {headerVariant === 'subpage' ? (
        <SubPageHeader
          title="Select emoji"
          onBack={onBack}
        />
      ) : (
        <>
          {/* Header carries the back chevron only; the title is the page
              headline below it — the same shape Change profile image and
              Add/Edit child now use. This screen kept the old 16px centred
              header-bar title, which QA filed as "Select emoji screen title
              doesn't match Figma format" (YAR-158). */}
          <div className="sticky top-0 z-[100] bg-background">
            <div className="flex items-center px-4 py-3">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
                aria-label="Go back"
              >
                <ChevronLeft size={24} />
              </button>
            </div>
          </div>

          {/* Sentence case per the Dev Notes global rule — it shipped as
              "Select Emoji". */}
          <h1
            id="select-emoji-heading"
            className="px-5 pt-2 text-[28px] leading-8 font-bold text-text-hero"
          >
            Select emoji
          </h1>
        </>
      )}

      <div className="flex-1 px-5 py-6 flex flex-col items-center gap-8">
        <Avatar
          emoji={selectedEmoji}
          size="xl"
          className={selectedColor}
        />
        <EmojiPicker
          className="w-full"
          selectedEmoji={selectedEmoji}
          selectedColor={selectedColor}
          onEmojiChange={setSelectedEmoji}
          onColorChange={setSelectedColor}
        />
      </div>

      {/* No divider above the CTA — "line above 'Save changes' ... shouldn't
          be there" (YAR-158), the same bullet already fixed on the child
          form's sticky footer. */}
      <div className="sticky bottom-0 bg-background px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button
          variant="primary"
          fullWidth
          onClick={() => onSave(selectedEmoji, selectedColor)}
        >
          Save changes
        </Button>
      </div>
    </div>
  );
}
